import { useEffect, useState } from 'react'
import { useAuth } from '../lib/auth'
import { adminStopActingAs } from '../lib/admin'

/**
 * Says whose account you are in.
 *
 * While a grant is live the app is indistinguishable from the real thing —
 * that is the whole point of act-as, and also the whole risk of it. Nothing
 * else on screen would tell you that the tour you just created belongs to
 * somebody else, so this is permanent, unmissable, and carries the way out.
 *
 * It shows the countdown rather than only the account, because the grant dies
 * on its own and a write refused with no explanation is a worse experience
 * than a clock that was visible all along.
 */
export default function ActingBanner() {
  const { acting, actingName, refreshClaims } = useAuth()
  const [releasing, setReleasing] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  // Read from the account record, with the value stashed at grant time as a
  // fallback. It used to come from sessionStorage alone, which a refresh in a
  // new tab does not carry — so the bar lost the name it exists to show.
  const name =
    actingName ??
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('encore.actingAs') : null)

  useEffect(() => {
    if (!acting) return
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [acting])

  if (!acting) return null

  // An open-ended grant is the beta default. The banner has to carry the whole
  // weight of "you are not in your own account" on its own now, so it says
  // that plainly rather than showing a clock that never moves.
  const openEnded = acting.until === 0
  const minutes = Math.ceil(Math.max(0, acting.until - now) / 60_000)

  const release = async () => {
    setReleasing(true)
    try {
      await adminStopActingAs({})
      sessionStorage.removeItem('encore.actingAs')
      await refreshClaims()
      // A hard reload rather than a re-render. Every listener on the page is
      // scoped to the account being left, and unwinding them individually is
      // more ways to get it subtly wrong than it is worth.
      window.location.assign('/admin/prospects')
    } catch {
      setReleasing(false)
    }
  }

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 16px',
        background: 'var(--accent)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: '#fff',
          flex: 'none',
        }}
      />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name ? `Acting as ${name}` : 'Acting as another account'}
      </span>
      <span
        style={{
          marginLeft: 'auto',
          opacity: 0.85,
          whiteSpace: 'nowrap',
          fontWeight: 500,
        }}
      >
        {openEnded ? 'Changes save to their account' : `${minutes}m left`}
      </span>
      <button
        type="button"
        onClick={release}
        disabled={releasing}
        style={{
          border: '1px solid rgba(255,255,255,0.55)',
          background: 'transparent',
          color: '#fff',
          borderRadius: 999,
          padding: '4px 12px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {releasing ? 'Leaving…' : 'Leave'}
      </button>
    </div>
  )
}
